import '../../Gallery.sass'

import { APIRequestErrorType, APIRequestState } from 'constants/API'
import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { AssignmentDetailsCard, AutomateOrderCard, ChangeRemunerationDrawer, CreativeAssignmentCard, ExpressBroadcastCard, GalleryAdminCommentSection, GalleryBreadcrumbs, GalleryDownloadStatus, GalleryProductFeedback, GalleryUploadErrorPopup, GalleryVirtualVisitInstructions, SubmitAssignmentModal, UpsellPayment, VirtualVisitHostingInfo } from '../../../common'
import { Box, Stack } from '@mui/material'
import { Color, ColorClass, IconType } from 'constants/assets'
import { ESProduct, ProductKind } from 'constants/product'
import { ErrorMessageType, errorMessageType, getDownloadErrorsString } from 'utils/helpers'
import { FC, Fragment, KeyboardEvent, MouseEvent as ReactMouseEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { KeyboardEventKey, ZERO_DECIMAL } from 'constants/misc'
import { POST_WATERMARK, VisualFileType } from 'constants/visual'
import { Trans, useTranslation } from 'react-i18next'
import { TypeOfAvailableOrderings, TypeOfAvailableVirtualVisitLinkTypes, VisualTypeSelectEnumType, useGallery, useGalleryAdditionalVisuals, useGalleryAssignment, useGalleryComments, useGalleryConstants, useGalleryDeal, useGalleryDocumentsContext, useGalleryEditorProduct, useGalleryOrder, useGalleryProduct, useGalleryVirtualVisit, useGalleryVisualSelection, useGalleryVisualType, useGalleryVisuals, useGalleryVisualsFileAPI, useGalleryVisualsMeta, useStagesUnlockingSendToClient } from '../../contexts'
import { decimalFromBig, formatPrice } from 'utils/price'
import { mutationToAPIRequest, queryToAPIRequest } from 'utils/reactQuery/queryHelpers'
import { useArchiveVisuals, useDownloadArchive } from 'dataQueries/visual.query'
import { useSubmitAssignment, useUpdateVirtualVisit } from 'dataQueries'

import { AdminGalleryDocumentsPanel } from '../AdminGalleryDocuments'
import { AdminGalleryMessageThreads } from '../AdminGalleryMessageThreads'
import { AssignmentDTOIsAdministratorDTO, VisualListResponseIsAdministratorResponse } from 'utils/typeguards'
import { AssignmentDetailDrawer } from 'components/common/Gallery/AssignmentDetailDrawer'
import AutoFireActionInput from 'components/common/AutoFireActionInput/AutoFireActionInput'
import { Badge } from 'components/common/Badges'
import BlockInfo from 'components/common/BlockInfo/BlockInfo'
import Button from 'components/common/Button/Button'
import { CopyButton } from 'components/common/CopyButton'
import { DocumentGalleryContent } from 'components/common/DocumentGalleryContent'
import { DownloadToastHandler } from 'components/common/Gallery/DownloadToastHandler'
import DynamicQueryContent from 'components/common/DynamicQueryContent/DynamicQueryContent'
import ErrorMessage from 'components/common/ErrorMessage/ErrorMessage'
import GalleryImage from 'components/common/GalleryImage/GalleryImage'
import Icon from 'components/common/Icon/Icon'
import { InputWrap } from 'components/common/InputWrap'
import { Link } from 'react-router-dom'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUITooltip } from 'components/common/MUITooltip'
import Modal from 'components/common/Modals/Modal/Modal'
import { Nullable } from 'models/helpers'
import { OrderDetail } from 'components/common/OrderDetail/OrderDetail'
import { PIPEDRIVE_INFINITY } from 'constants/pipedrive'
import { PageTab } from 'components/common/Page/PageTab'
import { PageTabs } from 'components/common/Page/PageTabs'
import { Path } from 'constants/router'
import ProgressCard from 'components/common/ProgressCard/ProgressCard'
import { QueryStatus } from 'components/common/QueryStatus'
import ReactLoading from 'react-loading'
import { RemoveDeliveredVisualsModal, canRemoveDeliveredVisuals } from '../RemoveDeliveredVisuals'
import { RemoveDeliveredVisualsResult } from 'dataQueries/assignmentVisuals.query'
import { SendVisualsToClient } from '../SendToClient'
import { StripeError } from '@stripe/stripe-js'
import TabLinkBar from 'components/common/TabLinkBar/TabLinkBar'
import TriangleIcon from 'components/common/TriangleIcon/TriangleIcon'
import { UploadImage } from 'models/redux'
import closeButtonStyles from 'components/styles/close-button.module.sass'
import { getPricingUrl } from 'utils/localization'
import i18n from 'translations/i18n'
import { identifiableProduct } from 'utils/serialization/productIdentifier'
import { invalidateListVisuals } from 'dataQueries/visual.query'
import { logger } from 'utils/logging'
import { multilineText } from 'utils/formatting'
import { removeLastFileExtension } from 'components/common/FileAPI/fileUtils'
import { supportEmail } from 'constants/contacts'
import { toVirtualVisitUrl } from 'utils/product/virtualVisitUrl'
import { useActionPopup } from 'utils/hooks/useActionPopup'
import { useAuth0 } from 'utils/auth'
import { useDraggingFile } from 'utils/hooks/useDraggingFile'
import { useImageRegex } from 'utils/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useUserData } from 'components/contexts/UserDataContext'
import { useVisualOperations } from 'utils/hooks/useVisualOperations'
import { useVisualStore } from 'stores/visualStore'
import { virtualFurnitureHomeLink } from 'constants/staticLinks'
import { wrapGrid } from 'animate-css-grid'

enum RawTabs {
  REQUIRED = 'REQUIRED',
  ADDITIONAL = 'ADDITIONAL',
}

export const AdministratorGalleryController: FC = () => {
  const queryClient = useQueryClient()

  const { assignmentId } = useGallery()

  const {
    NONE_EDITOR_PRODUCT,
    VisualTypeSelectEnum,
    AvailableFileTypes,
    AvailableOrderings,
    AvailableVirtualVisitLinkTypes,
    visualTypeIconMap,
    stagesUnlockingSendAssignmentVisualsToClient,
    stagesUnlockingCreativeSendAssignmentVisualsToAdmin,
    productKindsUnlockingMatterportLinks,
    beforeUnloadHandler,
  } = useGalleryConstants()
  const stagesUnlockingSendToClient = useStagesUnlockingSendToClient()

  const {
    assignment,
    assignmentData,
    product,
    assignmentStage,
    isAssignmentCancelled,
    canSelectVisualsForStaging,
    isBKBNDecide,
    isFeedbackButtonAllowed,
    logGalleryEvent,
    realtyId,
  } = useGalleryAssignment()

  const {
    dealData,
    dealAssignmentsForTab,
  } = useGalleryDeal()

  const {
    visualTypeSelect,
    setVisualTypeSelect,
    originalType,
    thumbnailType,
    webType,
    normalizedOriginalType,
    fileTypes,
    setFileTypes,
    transformTypes,
    transformAdminTypes,
  } = useGalleryVisualType()

  const {
    isVirtualVisit,
    isFloorPlan,
    isVirtualVisitMatterport,
    isVirtualVisitFloorfy,
    isDocumentProduct
  } = useGalleryProduct()

  const {
    setCurrentOrder,
    currentAdjustedOrder,
    sortEntriesFunction,
  } = useGalleryOrder()

  const {
    listVisualsQuery,
    allVisualsFilenames,
    purchasedVisualsKeys,
    purchasedVisualsCount,
    purchasedVisualsExist,
    uploadMinCount,
    uploadMaxCount,
    prepurchasedVisualsTotal,
    selectedVisualsForStagingCount,
    selectedVisualsForStagingExist,
    visualsMetadataMap,
  } = useGalleryVisualsMeta()

  const {
    downloadVisuals,
    downloadVisualsEntries,
    uploadVisualsEntries,
    downloadVisualsKeys,
    downloadVisualsCount,
    uploadVisualsCount,
    uploadVisualsOriginalFilenames,
    visualsCount,
    listedVisuals,
    visualsKeyToOriginalNameDictionary,
    downloadVisualsWithErrorEntries,
  } = useGalleryVisuals()

  const { currentlyVisibleVisuals } = useGalleryVisualsFileAPI()
  const deletingFilenames = useVisualStore(state => state.deletingFiles[assignmentId])

  const {
    virtualVisitShareLinkRef,
    virtualVisitEmbedCodeRef,
    selectedVirtualVisitLinkType,
    setSelectedVirtualVisitLinkType,
    virtualVisitInput,
    setVirtualVisitInput,
    virtualVisitURL,
    virtualVisitShareLink,
    virtualVisitExpirationDate,
    virtualVisitExpired,
    virtualVisitMatterportEmail,
    virtualVisitEmbedCode,
  } = useGalleryVirtualVisit()

  const {
    selected,
    setSelected,
    canSelectVisuals,
    selectableVisualsKeys,
    selectableVisualsCount,
    selectAll,
    selectAllPurchased,
    deselectAll,
    toggleSelectOnImage,
    selectedNotPurchasedVisualsCount,
    selectVisualsRequest,
    selectVisualsMutation,
  } = useGalleryVisualSelection()

  const {
    currency,
    pricePerAdditionalVisual,
    vat,
    discount,
    selectedAdditionalVisualsCount,
    totalForAdditionalVisualsBeforeDiscount,
    totalForAdditionalVisualsAfterDiscount,
    totalVatForAdditionalVisuals,
    totalAfterVatForAdditionalVisuals,
    isAdditionalVisualPurchaseDisabled,
    additionalVisualProductId,
    isAdditionalVisualsPurchaseModalOpen,
    setIsAdditionalVisualsPurchaseModalOpen,
  } = useGalleryAdditionalVisuals()

  const {
    adminCommentsForEditor,
    setAdminCommentsForEditor,
    adminCommentsForEditorResponse,
    editAdminCommentsForEditorMutation,
  } = useGalleryComments()

  const {
    editorProducts,
    editorProduct,
    setEditorProduct,
    sendAssignmentVisualsToEditor,
    canSendAssignmentVisualsToEditor,
    assignmentVisualsAlreadySentToEditor,
    cannotSendAssignmentVisualsToEditorInfo,
    canEditEditorProduct,
    canEditEditorComments,
    showNoOpenEsoftOrderNotice,
    initiateSendAssignmentVisualsToEditor,
    openEsoftOrderReference,
    showSendAnyway,
    canSendAnyway,
    cannotSendAnywayInfo,
    initiateSendAnyway,
  } = useGalleryEditorProduct()

  const { documentTypeSelect } = useGalleryDocumentsContext()

  const { uploadVisual, uploadVisualReplacement: uploadReplacement, deleteVisual } = useVisualOperations()
  const { t } = useTranslation(['gallery', 'deal_assignment', 'common', 'gallery_staging_flow'])
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()

  // REACT QUERIES
  const submitAssignment = useSubmitAssignment()
  const updateVirtualVisit = useUpdateVirtualVisit()

  const { showConfirm } = useActionPopup()
  const { roles } = useAuth0()
  const {
    baseUserData,
    clientData,
    creativeData,
    nonPersonalUserWorkspaces,
    currentUserWorkspace,
  } = useUserData()

  const isDraggingFile = useDraggingFile()
  const [visualsGrid, setVisualsGrid] = useState<HTMLDivElement | undefined>(undefined)
  const setVisualsGridRef = useCallback((node: HTMLDivElement) => {
    if (node) setVisualsGrid(node)
  }, [])

  const visualUploadingTriggered = useRef(false)

  const [isLightboxModalOpen, setLightboxModalOpen] = useState(false)
  const [isClientDownloadTriggered, setIsClientDownloadTriggered] = useState(false)
  const [expandedImageName, setExpandedImageName] = useState<string | undefined>(undefined)
  const [isSubmitAssignmentOpen, setIsSubmitAssignmentOpen] = useState(false)
  const [isRemoveDeliveredVisualsOpen, setIsRemoveDeliveredVisualsOpen] = useState(false)
  const [isUploadErrorPopupOpen, setIsUploadErrorPopupOpen] = useState(false)

  const [currentRawTab, setCurrentRawTab] = useState<RawTabs>(RawTabs.REQUIRED)
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null)

  const selectedAssignment = useMemo(
    () => dealData?.missions.find(a => a.id === selectedAssignmentId) ?? null,
    [dealData?.missions, selectedAssignmentId]
  )

  // Client archive flow
  const archiveClientMutation = useArchiveVisuals()
  const clientJobId = archiveClientMutation.data?.data?.id || null
  const downloadClientQuery = useDownloadArchive(assignmentId, clientJobId)
  const initiateDownloadVisualsArchive = useMemo(() => archiveClientMutation.isIdle ? null : mutationToAPIRequest(archiveClientMutation), [archiveClientMutation])
  const downloadVisualsArchive = useMemo(() => archiveClientMutation.isIdle ? null : queryToAPIRequest(downloadClientQuery, {
    treatDataAsPending: (data) => data == null
  }), [archiveClientMutation.isIdle, downloadClientQuery])

  // Admin archive flow
  const archiveAdminMutation = useArchiveVisuals()
  const adminJobId = archiveAdminMutation.data?.data?.id || null
  const downloadAdminQuery = useDownloadArchive(assignmentId, adminJobId, true)
  const archiveAdmin = useMemo(() => archiveAdminMutation.isIdle ? null : mutationToAPIRequest(archiveAdminMutation), [archiveAdminMutation])
  const downloadVisualsArchiveAdmin = useMemo(() => archiveAdminMutation.isIdle ? null : queryToAPIRequest(downloadAdminQuery, {
    treatDataAsPending: (data) => data == null
  }), [archiveAdminMutation.isIdle, downloadAdminQuery])

  // Admin functionalities
  const visualsRecentlySubmited = useMemo(() => submitAssignment.isPending || submitAssignment.isSuccess, [submitAssignment])
  const canSubmitVisualsForStaging = useMemo(() => !visualsRecentlySubmited && selected.size !== 0 && uploadVisualsCount === 0, [visualsRecentlySubmited, selected.size, uploadVisualsCount])
  const cannotSubmitVisualsForStagingInfo = useMemo(() => {
    if (visualsRecentlySubmited) return t('assignment_submitted')
    else if (selected.size === 0) return t('no_visuals_selected')
    else return ''
  }, [selected.size, visualsRecentlySubmited, t])

  const canSendAssignmentVisualsToClient = useMemo(() => {
    return ((isVirtualVisit && !!virtualVisitURL) || (visualsCount >= 1)) &&
      !!assignmentStage &&
      stagesUnlockingSendToClient.has(assignmentStage)
  }, [assignmentStage, isVirtualVisit, stagesUnlockingSendToClient, virtualVisitURL, visualsCount])

  const canDeleteVisuals = useMemo(() => !isVirtualVisit, [isVirtualVisit])
  const canUploadVisuals = useMemo(() => !isVirtualVisit && originalType !== POST_WATERMARK, [isVirtualVisit, originalType])
  const canReplaceVisuals = useMemo(() => originalType !== POST_WATERMARK, [originalType])
  const canSubmitVisuals = useMemo(() => {
    if (!assignmentStage) return false
    if (!stagesUnlockingCreativeSendAssignmentVisualsToAdmin.has(assignmentStage)) return false
    if (!!product) return !productKindsUnlockingMatterportLinks.has(product?.kind)
    return true
  }, [product, productKindsUnlockingMatterportLinks, assignmentStage, stagesUnlockingCreativeSendAssignmentVisualsToAdmin])
  const downloadArchiveUrl = useMemo(() => downloadClientQuery.data?.url, [downloadClientQuery.data])
  const downloadArchiveAdminUrl = useMemo(() => downloadAdminQuery.data?.url, [downloadAdminQuery.data])
  const showMatterportTransferInfo = useMemo(() => !!assignmentStage && stagesUnlockingSendAssignmentVisualsToClient.has(assignmentStage) && isVirtualVisit && virtualVisitMatterportEmail, [assignmentStage, stagesUnlockingSendAssignmentVisualsToClient, isVirtualVisit, virtualVisitMatterportEmail])
  const archiveDownloadPending = useMemo(() => {
    if (initiateDownloadVisualsArchive) {
      if (initiateDownloadVisualsArchive.state === APIRequestState.ERROR) return false
      if (downloadVisualsArchive?.state === APIRequestState.ERROR) return false
      if (downloadVisualsArchive?.state === APIRequestState.OK) return false
      return true
    } else return false
  }, [initiateDownloadVisualsArchive, downloadVisualsArchive])
  const archiveAdminDownloadPending = useMemo(() => {
    if (archiveAdmin) {
      if (archiveAdmin.state === APIRequestState.ERROR) return false
      if (downloadVisualsArchiveAdmin?.state === APIRequestState.ERROR) return false
      if (downloadVisualsArchiveAdmin?.state === APIRequestState.OK) return false
      return true
    } else return false
  }, [archiveAdmin, downloadVisualsArchiveAdmin])
  const availableVisualTypes = useMemo(() => {
    return Object.values(VisualTypeSelectEnum).map(type => VisualTypeSelectEnum[type as VisualTypeSelectEnumType])
  }, [VisualTypeSelectEnum])

  // Virtual visit
  const showVirtualVisitInstructions = useMemo(() => {
    if ((product?.kind === ProductKind.HD || product?.kind === ProductKind.SD || product?.kind === ProductKind.FLOORFY_VIRTUAL_TOUR) && originalType === VisualFileType.POST) return false

    return true
  }, [originalType, product?.kind])

  // Grid animations
  useLayoutEffect(() => {
    if (visualsGrid) wrapGrid(visualsGrid)
  }, [visualsGrid])

  useEffect(() => {
    if (uploadVisualsCount > 0) {
      visualUploadingTriggered.current = true
    }
  }, [uploadVisualsCount])

  // Clear selection when swapping between Required and Additional tabs
  useEffect(
    () => { deselectAll() },
    [currentRawTab, deselectAll]
  )

  useEffect(() => {
    // use uploadRef to check if the download process is after uploading or not. 
    // If so, wait until all the download visuals process finish then show the error msg popup if there's error in downloadEntries.
    if (visualUploadingTriggered.current && !isUploadErrorPopupOpen && downloadVisualsWithErrorEntries.length && (downloadVisualsCount === visualsCount)) {
      setIsUploadErrorPopupOpen(true)
      logGalleryEvent(AnalyticsEvent.UPLOAD_ERROR, {
        userEmail: creativeData?.email,
        userType: 'admin',
        errorReason: getDownloadErrorsString(downloadVisualsWithErrorEntries, thumbnailType),
      })
    }
  }, [creativeData?.email, downloadVisualsCount, downloadVisualsWithErrorEntries, downloadVisualsWithErrorEntries.length, isUploadErrorPopupOpen, logGalleryEvent, thumbnailType, visualsCount])

  const closeLightboxModal = useCallback((e?: ReactMouseEvent<Element, MouseEvent>) => {
    if (e) e.preventDefault()
    setLightboxModalOpen(false)
    setTimeout(() => {
      setExpandedImageName(undefined)
    }, 500)
  }, [])

  const onFloorPlannerLinkClick = useCallback((e: React.MouseEvent<Element, MouseEvent>) => {
    e.stopPropagation()

    logAnalyticsEvent(AnalyticsEvent.FLOOR_PLANNER_LINK_CLICKED, {
      galleryType: 'ADMIN',
      assignmentId: assignmentData?.id,
      productCategory: product?.category,
      productKind: product?.kind,
    })
  }, [assignmentData?.id, product?.category, product?.kind])

  const expandedImage = useMemo(() => {
    if (!expandedImageName) return undefined
    if (!downloadVisuals?.[expandedImageName]) return undefined
    return downloadVisuals[expandedImageName]
  }, [expandedImageName, downloadVisuals])

  const expandedImageWebType = useMemo(() => expandedImage?.[webType], [expandedImage, webType])

  const switchExpandedImage = useCallback((direction: 'next' | 'previous') => {
    if (!expandedImage) return
    if (!downloadVisuals) return

    const isDirectionNext = direction === 'next'

    if (downloadVisualsCount === 0) return
    let chooseThisOne = false

    for (let i = isDirectionNext ? 0 : downloadVisualsCount - 1; isDirectionNext ? i < downloadVisualsCount : i >= 0; isDirectionNext ? i++ : i--) {
      if (chooseThisOne) return setExpandedImageName(downloadVisualsKeys[i])
      if (expandedImageName === downloadVisualsKeys[i]) chooseThisOne = true
    }

    return setExpandedImageName(isDirectionNext ? downloadVisualsKeys[0] : downloadVisualsKeys[downloadVisualsCount - 1])
  }, [expandedImage, expandedImageName, downloadVisuals, downloadVisualsKeys, downloadVisualsCount])

  const deleteSingleVisual = useCallback((visualKey: string) => {
    if (!canDeleteVisuals) return
    if (!visualKey) return
    if (selected.has(visualKey)) {
      selected.delete(visualKey)
      setSelected(new Set(selected))
    }
    deleteVisual({ roles, workspaceId: '', assignmentId, filename: visualKey, type: normalizedOriginalType })
  }, [canDeleteVisuals, selected, deleteVisual, roles, assignmentId, normalizedOriginalType, setSelected])

  const onDeliveredVisualsRemoved = useCallback(({ removed, failed }: RemoveDeliveredVisualsResult) => {
    setIsRemoveDeliveredVisualsOpen(false)
    deselectAll()
    if (removed.length > 0) spawnSuccessToast(t('remove_delivered_visuals_success', { count: removed.length }))
    if (failed.length > 0) {
      spawnErrorToast(t('remove_delivered_visuals_partial', { filenames: failed.join(', ') }))
      logger.warn('Delivered visuals removal recorded but some stored copies remain', undefined, {
        context: { component: 'AdministratorGallery', action: 'removeDeliveredVisuals', metadata: { assignmentId, removed: removed.length, failed: failed.length } },
      })
    }
  }, [assignmentId, deselectAll, spawnErrorToast, spawnSuccessToast, t])

  // The client's own purchases, without the self-edited visuals the gallery also shows as purchased
  const clientPurchasedFilenames = useMemo(() => new Set(
    listVisualsQuery.data && VisualListResponseIsAdministratorResponse(listVisualsQuery.data) ? listVisualsQuery.data.purchasedFilenames : []
  ), [listVisualsQuery.data])

  const deleteAllVisuals = useCallback(async () => {
    if (!canDeleteVisuals) return
    if (!(await showConfirm(t('delete_all_prompt')))) return
    if (downloadVisuals) {
      for (let downloadKey of downloadVisualsKeys) {
        deleteVisual({ roles, workspaceId: '', assignmentId, filename: downloadKey, type: normalizedOriginalType })
      }
    }
    if (uploadVisualsEntries) {
      for (let entry of uploadVisualsEntries) {
        const uploadImage = entry[1]?.[normalizedOriginalType]
        if (!uploadImage?.file?.name) continue
        deleteVisual({ roles, workspaceId: '', assignmentId, filename: uploadImage.file.name, type: normalizedOriginalType })
      }
    }
  }, [t, canDeleteVisuals, downloadVisuals, uploadVisualsEntries, roles, assignmentId, deleteVisual, downloadVisualsKeys, normalizedOriginalType, showConfirm])

  const lightboxModalKeyDownHandler = useCallback((e: KeyboardEvent) => {
    switch (e.key) {
      // escape
      case KeyboardEventKey.ESCAPE:
        return closeLightboxModal()
      // spacebar
      case KeyboardEventKey.SPACE:
        if (!expandedImage || !expandedImageName) return
        return toggleSelectOnImage(expandedImageName)
      // left key
      case KeyboardEventKey.ARROW_LEFT:
        return switchExpandedImage('previous')
      // right key
      case KeyboardEventKey.ARROW_RIGHT:
        return switchExpandedImage('next')
    }
  }, [expandedImage, expandedImageName, closeLightboxModal, toggleSelectOnImage, switchExpandedImage])

  const onDrop = useCallback(async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections && fileRejections.length > 0) {
      const formats: string[] = []
      for (let fileRejection of fileRejections) {
        const split = fileRejection.file.name.split('.')
        if (split.length < 2) formats.push(fileRejection.file.name)
        else formats.push(split.slice(1).join('.'))
      }
      const formatsString = Array.from(new Set(formats).keys()).join(', ')
      alert(`${t('unsupported_file_format')}\n${formatsString}`)
    }

    const duplicateFiles = []
    for (let file of acceptedFiles) {
      if (!uploadVisualsOriginalFilenames.includes(file.name)) {
        uploadVisual({ roles, workspaceId: '', assignmentId, file, type: normalizedOriginalType, uploadType: 'resumable' })
      } else duplicateFiles.push(file.name)
    }
    if (duplicateFiles.length > 0) {
      alert(t('duplicate_file_alert', { count: duplicateFiles.length, files: duplicateFiles.join(', ') }))
    }
  }, [t, uploadVisual, normalizedOriginalType, uploadVisualsOriginalFilenames, assignmentId, roles])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, disabled: originalType === VisualFileType.RAW && currentRawTab !== RawTabs.REQUIRED })

  // Warn about upload being in progress while leaving the browser tab
  useEffect(() => {
    if (uploadVisualsCount > 0) {
      window.addEventListener('beforeunload', beforeUnloadHandler)
      return () => {
        window.removeEventListener('beforeunload', beforeUnloadHandler)
      }
    }
  }, [beforeUnloadHandler, uploadVisualsCount])

  // Warn about archive download in progress while leaving the browser tab
  useEffect(() => {
    if (archiveDownloadPending || archiveAdminDownloadPending) {
      window.addEventListener('beforeunload', beforeUnloadHandler)
      return () => {
        window.removeEventListener('beforeunload', beforeUnloadHandler)
      }
    }
  }, [archiveDownloadPending, archiveAdminDownloadPending, beforeUnloadHandler])

  const isArchiveDisabled = useMemo(() => {
    if (initiateDownloadVisualsArchive && initiateDownloadVisualsArchive.state === APIRequestState.RUNNING) return true
    if (!product) return true
    if (purchasedVisualsExist) return false
    if (selected.size === 0) return true
    if (prepurchasedVisualsTotal !== PIPEDRIVE_INFINITY) {
      if (selected.size < prepurchasedVisualsTotal) return true
      if (selected.size > prepurchasedVisualsTotal && isAdditionalVisualPurchaseDisabled) return true
    }
    return false
  }, [selected, prepurchasedVisualsTotal, initiateDownloadVisualsArchive, product, purchasedVisualsExist, isAdditionalVisualPurchaseDisabled])

  const imageNameReplacePattern = useImageRegex(assignmentId)

  // TODO: should be removed from Admin gallery as it's purely client mechanic admins have their own download section
  const initiateArchiveDownload = useCallback(async () => {
    if (isArchiveDisabled) return
    if (!product) return

    // The selection wire identifies the assignment product by its Product2 code alone, so a product
    // without one cannot be selected at all — say so rather than fail the request silently.
    const assignmentProduct = identifiableProduct(product.productCode)

    if (!assignmentProduct) {
      spawnErrorToast(t('gallery_staging_flow:upsell_abort_error'))
      logger.error('Visuals not selected - the assignment product carries no product code', undefined, {
        context: { component: 'AdministratorGallery', action: 'initiateArchiveDownload', metadata: { assignmentId, productId: product.id } },
      })

      return
    }

    // If there is limit for downloading/selecting visuals, proceed with additional checks
    if (prepurchasedVisualsTotal !== PIPEDRIVE_INFINITY) {

      // If additional visuals are selected, open purchase modal
      if (selectedAdditionalVisualsCount > 0) {
        setIsAdditionalVisualsPurchaseModalOpen(true)
        return
      }

      // If no additional visuals are selected and no visuals have been purchased yet
      // check, that required amount have been selected, show warning propmt and select those visuals with immediate download after selection completes
      if (!purchasedVisualsExist) {
        if (selected.size !== prepurchasedVisualsTotal || !(await showConfirm(t('purchase_warning_prompt'), { title: t('purchase_warning_prompt_title') }))) return
        selectVisualsMutation.mutate(
          { assignmentId, workspaceId: '', isClient: false, filenames: Array.from(selected), product: assignmentProduct },
          { onSuccess: () => archiveClientMutation.mutate({ assignmentId, types: transformTypes(), filenames: Array.from(selected), isAdmin: false }) }
        )
        // Start the download cycle (toast notifications)
        setIsClientDownloadTriggered(true)
        return
      }
    }

    // If there is no limit for downloading/selecting visuals, but no visuals have been purchased, select all visuals and download those selected
    if (!purchasedVisualsExist) {
      if (!allVisualsFilenames.length) console.warn('No visual filenames found!')
      selectVisualsMutation.mutate(
        { assignmentId, workspaceId: '', isClient: false, filenames: allVisualsFilenames, product: assignmentProduct },
        { onSuccess: () => archiveClientMutation.mutate({ assignmentId, types: transformTypes(), filenames: Array.from(selected), isAdmin: false }) }
      )
      // Start the download cycle (toast notifications)
      setIsClientDownloadTriggered(true)
      return
    }

    // If there is no limit for downloading/selecting visuals
    // OR no additional visuals are selected and visuals have already been purchased
    // Trigger downloading of selected visuals
    archiveClientMutation.mutate({ assignmentId, types: transformTypes(), filenames: Array.from(selected), isAdmin: false })

    // Start the download cycle (toast notifications)
    setIsClientDownloadTriggered(true)

    // Track archive download trigger
    logGalleryEvent(AnalyticsEvent.GALLERY_DOWNLOAD_TRIGGERED, {
      role: Array.from(roles.all.values()).join(','),
      workspaceIDs: nonPersonalUserWorkspaces.length ? nonPersonalUserWorkspaces.map(({ id }) => id).join(', ') : '',
      productCategory: product.category,
      productKind: product.kind,
      assignmentOwnerId: dealData?.customer.id,
      assignmentOwnerEmail: dealData?.customer.email,
    })

  }, [isArchiveDisabled, product, prepurchasedVisualsTotal, purchasedVisualsExist, archiveClientMutation, assignmentId, transformTypes, selected, logGalleryEvent, roles, nonPersonalUserWorkspaces, dealData?.customer.id, dealData?.customer.email, selectedAdditionalVisualsCount, setIsAdditionalVisualsPurchaseModalOpen, showConfirm, spawnErrorToast, t, selectVisualsMutation, allVisualsFilenames])

  /**
   * Sends the additional-visuals selection, identifying both products by their Product2 code. The
   * payment intent already exists here, so a product without a code aborts loudly rather than
   * posting a selection the API would reject.
   */
  const handleConfirmSelection = useCallback((paymentIntentId: string | undefined, billingProductCode: Nullable<string>) => {
    const assignmentProduct = identifiableProduct(product?.productCode)
    const billingProduct = identifiableProduct(billingProductCode)

    if (!assignmentProduct || !billingProduct) {
      spawnErrorToast(t('gallery_staging_flow:upsell_abort_error'))
      logger.error('Additional visuals selection not submitted - product carries no product code', undefined, {
        context: {
          component: 'AdministratorGallery',
          action: 'handleConfirmSelection',
          metadata: { assignmentId, hasAssignmentProductCode: !!assignmentProduct, hasBillingProductCode: !!billingProduct },
        },
      })

      return
    }

    selectVisualsMutation.mutate({
      assignmentId,
      workspaceId: '',
      isClient: false,
      filenames: Array.from(selected),
      product: assignmentProduct,
      billingProduct,
      paymentIntentId,
    })
  }, [assignmentId, product?.productCode, selectVisualsMutation, selected, spawnErrorToast, t])

  // Relist visuals if stripe payment fails and user leaves purchase modal without finishing the payment
  const handleAfterAdditionalPurchaseModalClose = useCallback((stripeError: StripeError | null) => {
    if (stripeError) {
      invalidateListVisuals(queryClient, assignmentId)
    }

    selectVisualsMutation.reset()
  }, [selectVisualsMutation, assignmentId, queryClient])

  const initiateArchiveAdminDownloadAll = useCallback(() => {
    if (downloadVisualsCount === 0) return
    archiveAdminMutation.mutate({ assignmentId, types: transformAdminTypes(), filenames: downloadVisualsKeys, isAdmin: true })
  }, [downloadVisualsCount, archiveAdminMutation, assignmentId, transformAdminTypes, downloadVisualsKeys])

  const initiateArchiveAdminDownloadSelected = useCallback(() => {
    if (selected.size === 0) return
    archiveAdminMutation.mutate({ assignmentId, types: transformAdminTypes(), filenames: Array.from(selected), isAdmin: true })
  }, [selected, archiveAdminMutation, assignmentId, transformAdminTypes])

  const initiateArchiveAdminDownloadPurchased = useCallback(() => {
    if (purchasedVisualsCount === 0) return
    archiveAdminMutation.mutate({ assignmentId, types: transformAdminTypes(), filenames: Array.from(purchasedVisualsKeys), isAdmin: true })
  }, [purchasedVisualsCount, archiveAdminMutation, assignmentId, transformAdminTypes, purchasedVisualsKeys])

  const initiateSubmitAssignment = useCallback(async (creativeNote: Nullable<string>) => {
    if (!canSubmitVisuals) return
    if (visualsRecentlySubmited) return
    if (!product) return
    if (isVirtualVisit) {
      submitAssignment.mutate({
        assignmentId,
        requiredVisualsFilenames: [],
        additionalVisualsFilenames: [],
        creativeCommentForBackbone: creativeNote,
        product,
        minUploadCount: uploadMinCount,
      })
    } else {
      if (uploadVisualsCount > 0) return
      if (selected.size === 0) return

      submitAssignment.mutate({
        assignmentId,
        requiredVisualsFilenames: Array.from(selected),
        additionalVisualsFilenames: [],
        creativeCommentForBackbone: creativeNote,
        product,
        minUploadCount: uploadMinCount,
      })
    }
  }, [canSubmitVisuals, visualsRecentlySubmited, product, isVirtualVisit, assignmentId, uploadMinCount, uploadVisualsCount, selected, submitAssignment])

  const errorElement = useCallback((errorType: APIRequestErrorType) => (
    <div className="page-error">
      <h1 className="error-heading">{t('forbidden_access_heading')}</h1>
      {(errorType === APIRequestErrorType.FORBIDDEN_ERROR || APIRequestErrorType.PAYMENT_REQUIRED_ERROR) ?
        <div>
          <Trans t={t} i18nKey="forbidden_access">
            <span>
              <a href={getPricingUrl(i18n.language)} target="_blank" rel="noreferrer" className="underline">&nbsp;</a>
            </span>
          </Trans>
          <BlockInfo className="margin-top">
            <Trans t={t} i18nKey={'forbidden_contact_us'}>
              <span>
                <a href={`mailto:${supportEmail}`} className="underline">&nbsp;</a>
              </span>
            </Trans>
          </BlockInfo>
        </div>
        :
        <p><ErrorMessage error_type={errorType} /></p>
      }
      <Link to={Path.INDEX}>
        <Button className="back-button">{t('back_to_my_orders')}</Button>
      </Link>
    </div>
  ), [t])

  const preloadListing = useCallback(() => {
    const tupleArray: [string, string, JSX.Element][] = listedVisuals.map(visual => {
      const filename = visual.name.replace(imageNameReplacePattern, '') + (visual.originalName ? ` | ${visual.originalName}` : '')
      const reactKey = visual.name
      return [visual.name, reactKey, (
        <div className="image-wrap">
          <GalleryImage
            image={undefined}
            label={filename}
            wait={true}
            faded={true}
            expandable={false}
            purchased={purchasedVisualsKeys.has(visual.name)}
            visualMetadata={visualsMetadataMap[visual.name]}
            deletable={canDeleteVisuals}
            onDelete={() => visual.name && deleteSingleVisual(visual.name)}
            onFloorPlannerLinkClick={onFloorPlannerLinkClick}
          >
            <div className="text">
              <ReactLoading color={Color.GRAY_TEXT} type="cylon" className="spinner" />
              <span>{t('loading')}</span>
            </div>
          </GalleryImage>
        </div>
      )]
    })

    return tupleArray
  }, [listedVisuals, imageNameReplacePattern, purchasedVisualsKeys, visualsMetadataMap, canDeleteVisuals, onFloorPlannerLinkClick, t, deleteSingleVisual])

  const downloadListing = useCallback(() => {
    const tupleArray: [string, string, JSX.Element][] = downloadVisualsEntries.map(([key, allTypes]) => {
      const img = allTypes?.[thumbnailType]
      if (!img) throw new Error(`Image does not exist for key: ${key} and visual type: ${thumbnailType}`)
      const isError = img.request.error_type !== APIRequestErrorType.NONE
      const isSelectionAllowed = originalType === VisualFileType.RAW || !isError
      const errorMsg = img.request.error
      const isOtherMsg = errorMessageType(errorMsg) === ErrorMessageType.OTHER
      const filename = img.file?.name || ''
      const originalFilename = img.originalFilename || !!filename && visualsKeyToOriginalNameDictionary[filename] || ''
      const label = filename.replace(imageNameReplacePattern, '') + (originalFilename ? ` | ${originalFilename}` : '')
      const reactKey = key
      return [key, reactKey, (
        <div className="image-wrap">
          <GalleryImage
            image={img.signedUrl}
            label={label}
            wait={img.deleting}
            faded={img.deleting}
            deletable={canDeleteVisuals}
            onDelete={() => img.file?.name && deleteSingleVisual(img.file.name)}
            selectable={canSelectVisuals && isSelectionAllowed}
            selected={isSelectionAllowed && selected.has(img.file?.name || '')}
            visualMetadata={visualsMetadataMap[img.file?.name || '']}
            onSelect={isSelectionAllowed && canSelectVisuals ? e => {
              toggleSelectOnImage(img.file?.name)
            } : undefined}
            expandable={!!(webType && allTypes?.[webType]?.signedUrl)}
            purchased={purchasedVisualsKeys.has(key)}
            onClick={e => {
              setExpandedImageName(img.file?.name)
              setLightboxModalOpen(true)
            }}
            droppable={canReplaceVisuals}
            isUserDraggingFile={canReplaceVisuals && isDraggingFile}
            dropZoneOptions={{ noClick: true, multiple: false }}
            onFloorPlannerLinkClick={onFloorPlannerLinkClick}
            insideHover={canReplaceVisuals && isDraggingFile ?
              <div className="text">
                <span>{t('drop_here')}</span>
              </div>
              :
              undefined
            }
            onDrop={canReplaceVisuals ? async (acceptedFiles, rejectedFiles) => {
              if (rejectedFiles.length === 1) return window.alert(t('reject_files'))
              if (acceptedFiles.length === 0) return window.alert(t('reject_files'))
              if (rejectedFiles.length > 1) return window.alert(t('drop_only_one'))
              if (acceptedFiles.length > 1) return window.alert(t('drop_only_one'))
              if (acceptedFiles.length === 1) {
                if (!img.file?.name) return
                if (await showConfirm(t('replace_visual_prompt'))) {
                  if (!uploadVisualsOriginalFilenames.includes(img.file.name)) {
                    uploadReplacement({ roles, workspaceId: '', assignmentId, file: acceptedFiles[0], type: normalizedOriginalType, uploadType: 'resumable', replaces: img.file.name })
                  } else {
                    alert(t('duplicate_file_alert', { count: 1, files: img.file.name }))
                  }
                }
              }
            } : undefined}
          >
            {img.deleting ?
              <div className="text">
                <ReactLoading color={Color.GRAY_TEXT} type="cylon" className="spinner" />
                <span>{t('deleting')}</span>
              </div>
              :
              isError ?
                <div className="text">
                  <span>
                    <Trans t={t} i18nKey="thumbnail_failed_description">
                      <MUITooltip placement="top" content={isOtherMsg ? errorMsg : t(`image_processing_error.${errorMsg}`)}>
                        <span style={{ cursor: 'pointer' }} data-tip data-for="re-upload-tooltip">&nbsp;</span>
                      </MUITooltip>
                    </Trans>
                  </span>
                </div>
                :
                <Fragment></Fragment>
            }
          </GalleryImage>
        </div>
      )]
    })

    return tupleArray
  }, [downloadVisualsEntries, thumbnailType, originalType, visualsKeyToOriginalNameDictionary, imageNameReplacePattern, canDeleteVisuals, canSelectVisuals, selected, visualsMetadataMap, webType, purchasedVisualsKeys, canReplaceVisuals, isDraggingFile, onFloorPlannerLinkClick, t, deleteSingleVisual, toggleSelectOnImage, showConfirm, uploadVisualsOriginalFilenames, uploadReplacement, assignmentId, normalizedOriginalType, roles])

  const uploadImageContent = useCallback((img: Nullable<UploadImage>, label: string) => {
    if (!img) return
    const isError = img.request.error_type !== APIRequestErrorType.NONE
    const errorMsg = img.request.error
    const isOtherMsg = errorMessageType(errorMsg) === ErrorMessageType.OTHER

    if (img.deleting) {
      return (
        <Fragment>
          <ReactLoading color={Color.GRAY_TEXT} type="cylon" className="spinner" />
          <span>{t('deleting')}</span>
        </Fragment>
      )
    }

    if (img.progress === 100) {
      if (isError) {
        return (
          <span>
            <Trans t={t} i18nKey="upload_failed_description">
              <MUITooltip placement="top" content={isOtherMsg ? errorMsg : t(`image_processing_error.${errorMsg}`)}>
                <span style={{ cursor: 'pointer' }} data-tip data-for="re-upload-tooltip">&nbsp;</span>
              </MUITooltip>
            </Trans>
          </span>
        )
      }

      return (
        <Fragment>
          <ReactLoading color={Color.GRAY_TEXT} type="cylon" className="spinner" />
          <span>{t('processing')}</span>
        </Fragment>
      )
    }

    return (
      <span>
        {img.originalFilename &&
          <Fragment>
            {label}
            <br />
          </Fragment>
        }
        {t('uploading')}: {img.progress || 0}%
      </span>
    )
  }, [t])

  const uploadListing = useCallback(() => {
    const tupleArray: [string, string, string, JSX.Element][] = []

    for (const [key, allTypes] of uploadVisualsEntries) {
      const img = allTypes?.[normalizedOriginalType]
      if (!img) continue

      // For sorting fresh uploads on top
      const sortKey = (img.replaces || key.match(imageNameReplacePattern)) ? key : `${assignmentId}-bkbn-ZZZZZ-${String(img.droppedIn).padStart(15, '0')}-${img.originalFilename || key}`
      const reactKey = img.originalFilename || key
      const fileName = img.file ? img.file.name.replace(imageNameReplacePattern, '') : ''
      const originalFilename = img.originalFilename
      const label = fileName ? fileName + (originalFilename ? ` | ${originalFilename}` : '') : originalFilename || ''

      // When upload is complete, show the FileAPI image directly (bypasses useMemo chain for reliability)
      const fileAPIEntry = img.progress === 100
        ? currentlyVisibleVisuals.find(v => v.gcFilename === key || v.gcFilename?.replace(/\.[^.]+$/, '') === key)
        : undefined
      const isComplete = !!(fileAPIEntry?.displayUrl)

      if (isComplete) {
        // Render a full download card (with all actions) at the upload's ZZZZZ position
        // Using FileAPI data directly for reliability (bypasses useMemo chain)
        const dlFilename = fileAPIEntry!.gcFilename || key
        const dlOriginalFilename = fileAPIEntry!.metadata?.originalName || fileAPIEntry!.originalFilename || ''
        const dlLabel = dlFilename.replace(imageNameReplacePattern, '') + (dlOriginalFilename ? ` | ${dlOriginalFilename}` : '')
        const dlIsDeleting = deletingFilenames?.has(dlFilename) ?? false

        tupleArray.push([key, reactKey, sortKey, (
          <div className="image-wrap">
            <GalleryImage
              image={fileAPIEntry!.displayUrl ?? undefined}
              label={dlLabel}
              wait={dlIsDeleting}
              faded={dlIsDeleting}
              deletable={canDeleteVisuals}
              onDelete={() => deleteSingleVisual(dlFilename)}
              selectable={canSelectVisuals}
              selected={selected.has(dlFilename)}
              visualMetadata={visualsMetadataMap[dlFilename]}
              onSelect={canSelectVisuals ? () => toggleSelectOnImage(dlFilename) : undefined}
              expandable={true}
              purchased={purchasedVisualsKeys.has(key)}
              onClick={() => {
                setExpandedImageName(dlFilename)
                setLightboxModalOpen(true)
              }}
              droppable={canReplaceVisuals}
              isUserDraggingFile={canReplaceVisuals && isDraggingFile}
              dropZoneOptions={{ noClick: true, multiple: false }}
              onFloorPlannerLinkClick={onFloorPlannerLinkClick}
              insideHover={canReplaceVisuals && isDraggingFile ?
                <div className="text">
                  <span>{t('drop_here')}</span>
                </div>
                :
                undefined
              }
              onDrop={canReplaceVisuals ? async (acceptedFiles, rejectedFiles) => {
                if (rejectedFiles.length === 1) return window.alert(t('reject_files'))
                if (acceptedFiles.length === 0) return window.alert(t('reject_files'))
                if (rejectedFiles.length > 1) return window.alert(t('drop_only_one'))
                if (acceptedFiles.length > 1) return window.alert(t('drop_only_one'))
                if (acceptedFiles.length === 1) {
                  if (!dlFilename) return
                  if (await showConfirm(t('replace_visual_prompt'))) {
                    if (!uploadVisualsOriginalFilenames.includes(dlFilename)) {
                      uploadReplacement({ roles, workspaceId: '', assignmentId, file: acceptedFiles[0], type: normalizedOriginalType, uploadType: 'resumable', replaces: dlFilename })
                    } else {
                      alert(t('duplicate_file_alert', { count: 1, files: dlFilename }))
                    }
                  }
                }
              } : undefined}
            >
              {dlIsDeleting &&
                <div className="text">
                  <ReactLoading color={Color.GRAY_TEXT} type="cylon" className="spinner" />
                  <span>{t('deleting')}</span>
                </div>
              }
            </GalleryImage>
          </div>
        )])
      } else {
        tupleArray.push([key, reactKey, sortKey, (
          <div className="image-wrap">
            <GalleryImage
              image={undefined}
              label={label}
              wait={img.deleting}
              faded={img.deleting}
              deletable={canDeleteVisuals}
              onDelete={() => img.file?.name && deleteSingleVisual(img.file.name)}
            >
              <div className="text">
                {uploadImageContent(img, label)}
              </div>
            </GalleryImage>
          </div>
        )])
      }
    }

    return tupleArray
  }, [uploadVisualsEntries, currentlyVisibleVisuals, deletingFilenames, normalizedOriginalType, imageNameReplacePattern, assignmentId, canDeleteVisuals, canSelectVisuals, canReplaceVisuals, isDraggingFile, selected, visualsMetadataMap, purchasedVisualsKeys, uploadVisualsOriginalFilenames, uploadImageContent, deleteSingleVisual, toggleSelectOnImage, setExpandedImageName, setLightboxModalOpen, onFloorPlannerLinkClick, showConfirm, uploadReplacement, roles, t])

  const additionalFilenamesSet = useMemo(
    () => new Set(assignmentData && AssignmentDTOIsAdministratorDTO(assignmentData) ? assignmentData.ctUploadedAdditionalVisuals.map((filename) => removeLastFileExtension(filename)) : []),
    // Only interested in id to trigger re-calc, having entire data object might cause unnecessary re-calc
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignmentData?.id]
  )

  const isRaw = originalType === VisualFileType.RAW

  const isInCurrentRawTab = useCallback((filename: string) => {
    const stripped = removeLastFileExtension(filename)
    return currentRawTab === RawTabs.REQUIRED
      ? !additionalFilenamesSet.has(stripped)
      : additionalFilenamesSet.has(stripped)
  }, [currentRawTab, additionalFilenamesSet])

  const filteredSelectableKeys = useMemo(() => {
    if (!isRaw) return selectableVisualsKeys
    return selectableVisualsKeys.filter(isInCurrentRawTab)
  }, [isRaw, selectableVisualsKeys, isInCurrentRawTab])

  const filteredPurchasedKeys = useMemo(() =>
    filteredSelectableKeys.filter(key => purchasedVisualsKeys.has(key)),
    [filteredSelectableKeys, purchasedVisualsKeys]
  )

  const handleSelectAll = useCallback(() => {
    if (isRaw) {
      setSelected(new Set(filteredSelectableKeys))
    } else {
      selectAll()
    }
  }, [isRaw, filteredSelectableKeys, setSelected, selectAll])

  const handleSelectAllPurchased = useCallback(() => {
    if (isRaw) {
      setSelected(new Set(filteredPurchasedKeys))
    } else {
      selectAllPurchased()
    }
  }, [isRaw, filteredPurchasedKeys, setSelected, selectAllPurchased])

  const visualsListing = useCallback(() => {
    const map: Map<string, [string, JSX.Element]> = new Map()
    if (isVirtualVisit) return map

    for (let [key, reactKey, node] of preloadListing()) map.set(key, [reactKey, node])
    for (let [key, reactKey, node] of downloadListing()) map.set(key, [reactKey, node])
    for (let [key, reactKey, sortKey, node] of uploadListing()) {
      map.delete(key)
      map.set(sortKey, [reactKey, node])
    }

    const entries: [string, [string, JSX.Element]][] = Array.from(map.entries())
    let sortedEntries = entries.sort(sortEntriesFunction)

    if (originalType === VisualFileType.RAW) {
      sortedEntries = sortedEntries.filter(([filename]) => isInCurrentRawTab(filename))
    }

    return (
      <Fragment>
        {sortedEntries.map(([, [reactKey, node]]) => (
          <Fragment key={reactKey}>
            {node}
          </Fragment>
        ))}
      </Fragment>
    )
  }, [isVirtualVisit, sortEntriesFunction, originalType, preloadListing, downloadListing, uploadListing, isInCurrentRawTab])

  const fileSelection = useCallback(() => (
    <Fragment>
      <div className="part">
        <Button
          type="secondary"
          disabled={selected.size === (isRaw ? filteredSelectableKeys.length : selectableVisualsCount)}
          onClick={handleSelectAll}
          textAndIcon={true}
          className="space-right space-bottom"
        >
          <Icon icon={IconType.CHECK} />
          <span>{t('select_all')}</span>
        </Button>

        {(isRaw ? filteredPurchasedKeys.length : purchasedVisualsCount) > 0 &&
          <Button
            type="secondary"
            disabled={selected.size === (isRaw ? filteredPurchasedKeys.length : purchasedVisualsCount)}
            onClick={handleSelectAllPurchased}
            textAndIcon={true}
            className="space-right space-bottom"
          >
            <Icon icon={IconType.CHECK} />
            <span>{t('select_all_purchased')}</span>
          </Button>
        }

        <Button
          type="secondary"
          disabled={selected.size === 0}
          onClick={deselectAll}
          textAndIcon={true}
        >
          <Icon icon={IconType.CROSS} />
          <span>{t('deselect_all')}</span>
        </Button>
      </div>
    </Fragment>
  ), [deselectAll, handleSelectAll, handleSelectAllPurchased, isRaw, filteredSelectableKeys.length, selectableVisualsCount, filteredPurchasedKeys.length, purchasedVisualsCount, selected.size, t])

  const fileTypeSelection = useCallback(() => (
    <Fragment>
      <div className="part">
        <p><strong>{t('select_file_type')}</strong></p>
        {Object.values(AvailableFileTypes).map(k => (
          <label key={k} className="checkbox square" htmlFor={`file_type_checkbox_${k}`}>
            <input
              type="checkbox"
              name="file_type_checkbox"
              id={`file_type_checkbox_${k}`}
              value={k}
              checked={fileTypes.has(k)}
              onChange={e => {
                if (fileTypes.has(k)) {
                  fileTypes.delete(k)
                  setFileTypes(new Set(fileTypes))
                } else {
                  setFileTypes(new Set(fileTypes.add(k)))
                }
              }}
            />
            <span className="checkmark"></span>
            <span className="label-after">
              <span>{t(`file_type.${k}`)}</span>

              <MUITooltip
                id={`file_type_tooltip_${k}`}
                type="secondary"
                content={
                  <div className="tooltip-content">
                    <span>{t(`file_type_tooltip.${k}`)}</span>
                  </div>
                }
              >
                <Icon
                  icon={IconType.QUESTION}
                  colorClass={ColorClass.SECONDARY_ORANGE_DARK}
                  className="tooltip-icon space-larger-left"
                />
              </MUITooltip>
            </span>
          </label>
        ))}
      </div>
    </Fragment>
  ), [t, AvailableFileTypes, fileTypes, setFileTypes])

  return (
    <Fragment>
      <div className="page gallery">
        <div className="page-content">
          {assignment &&
            <Fragment>
              <GalleryBreadcrumbs assignment={assignment.data} />
              <DynamicQueryContent
                query={assignment}
                showStates={['error']}
              >
                <DynamicQueryContent
                  query={listVisualsQuery}
                  showStates={['error']}
                >
                  <Fragment>
                    <div className="order-detail-wrapper">
                      {(assignmentData && dealData) ?
                        <OrderDetail
                          assignmentData={assignmentData}
                          dealData={dealData}
                        />
                        :
                        <Fragment></Fragment>
                      }
                    </div>

                    {!!dealData &&
                      <AutomateOrderCard />
                    }

                    {!!dealData?.expressDispatch &&
                      <ExpressBroadcastCard />
                    }

                    <div className="assignment-tabs">
                      <TabLinkBar assignments={dealAssignmentsForTab} activeAssignmentId={assignmentId}></TabLinkBar>
                    </div>

                    <div className="gallery-content">
                      <div className="details-wrapper">

                        <Fragment>
                          {assignmentStage ?
                            <Fragment>
                              {isDocumentProduct
                                ? <AdminGalleryDocumentsPanel
                                  assignmentId={assignmentId}
                                  assignment={assignmentData}
                                  product={product}
                                  onSeeDetails={() => setSelectedAssignmentId(assignmentId)}
                                />
                                : <Fragment>
                                  {/** SEVERAL PRODUCTS FEEDBACK BUTTON */}
                                  {isFeedbackButtonAllowed && <GalleryProductFeedback />}

                                  {originalType !== VisualFileType.RAW &&
                                    <div className="details">
                                      <div className="gallery-link-wrapper">
                                        <div className="gallery-link-info">
                                          <span>{t('link_copy_text')}</span>
                                        </div>
                                        <div className="gallery-link-button">
                                          <CopyButton
                                            copyText={window.location.href}
                                            textContent={t('link_copy')}
                                            onCopy={() => {
                                              logAnalyticsEvent('Use Copy Link', {
                                                userId: baseUserData?.id,
                                                workspaces: clientData?.workspaces,
                                                planId: currentUserWorkspace?.id,
                                                ...(realtyId && { realtyId }),
                                              })
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  }

                                  <Fragment>
                                    <AssignmentDetailsCard
                                      assignmentId={assignmentId}
                                      onSeeDetails={() => setSelectedAssignmentId(assignmentId)}
                                    />
                                    <CreativeAssignmentCard />
                                  </Fragment>

                                  {/** Header of admin section */}
                                  <div className="details">
                                    <div className="sticky">
                                      {availableVisualTypes.length === 1 ?
                                        <Fragment>
                                          <TriangleIcon type={ColorClass.PRIMARY_BLUE} icon={visualTypeIconMap[availableVisualTypes[0]]} />
                                        </Fragment>
                                        :
                                        <Fragment>
                                          {availableVisualTypes.length > 1 &&
                                            <div className="space-bottom-large flex">
                                              <div className="visualTypeTabs">
                                                {availableVisualTypes.map(type => (
                                                  <Button
                                                    key={type}
                                                    type={visualTypeSelect === type ? 'primary' : 'secondary'}
                                                    textAndIcon={true}
                                                    onClick={() => {
                                                      setVisualTypeSelect(type)
                                                      setSelected(new Set())
                                                    }}
                                                  >
                                                    <Icon icon={visualTypeIconMap[type]} />
                                                    <span>{t(`visual_type.admin.${type}`)}</span>
                                                  </Button>
                                                ))}
                                              </div>
                                            </div>
                                          }
                                        </Fragment>
                                      }

                                      <Fragment>
                                        {originalType !== POST_WATERMARK && !isVirtualVisit &&
                                          <Fragment>
                                            <select
                                              name="orderingSelect"
                                              id="orderingSelect"
                                              className="space-top"
                                              value={currentAdjustedOrder}
                                              onChange={e => {
                                                const newOrdering = (e.target.value as TypeOfAvailableOrderings)
                                                setCurrentOrder(newOrdering)
                                              }}
                                            >
                                              {Object.values(AvailableOrderings).map(ordering => (
                                                <option key={ordering} value={ordering}>{ordering}</option>
                                              ))}
                                            </select>
                                          </Fragment>
                                        }

                                        <br />

                                        {!isVirtualVisit &&
                                          <Fragment>
                                            <p>{t('admin_download')}</p>
                                            {fileTypeSelection()}
                                            <div className="part">
                                              <Button
                                                type="secondary blue"
                                                onClick={() => {
                                                  if (archiveAdminDownloadPending) return
                                                  initiateArchiveAdminDownloadAll()
                                                }}
                                                disabled={archiveAdminDownloadPending}
                                                className="space-right"
                                              >
                                                {t('download_all_admin', { count: downloadVisualsCount })}
                                              </Button>
                                              {archiveAdminDownloadPending &&
                                                <span>{t('download_requested')}</span>
                                              }
                                            </div>

                                            <div className="part">
                                              <Button
                                                type="secondary orange"
                                                onClick={() => {
                                                  if (archiveAdminDownloadPending) return
                                                  initiateArchiveAdminDownloadSelected()
                                                }}
                                                disabled={selected.size === 0 || archiveAdminDownloadPending}
                                                className="space-right"
                                              >
                                                {t('download_selected_admin', { count: selected.size })}
                                              </Button>
                                              {archiveAdminDownloadPending &&
                                                <span>{t('download_requested')}</span>
                                              }
                                            </div>

                                            {purchasedVisualsExist &&
                                              <div className="part">
                                                <Button
                                                  type="secondary green"
                                                  onClick={() => {
                                                    if (archiveAdminDownloadPending) return
                                                    initiateArchiveAdminDownloadPurchased()
                                                  }}
                                                  disabled={purchasedVisualsCount === 0 || archiveAdminDownloadPending}
                                                  className="space-right"
                                                >
                                                  {t('download_purchased_admin', { count: purchasedVisualsCount })}
                                                </Button>
                                                {archiveAdminDownloadPending &&
                                                  <span>{t('download_requested')}</span>
                                                }
                                              </div>
                                            }

                                            {archiveAdmin &&
                                              <GalleryDownloadStatus
                                                nopadding={true}
                                                isSimpleVersion={true}
                                                assignmentId={assignmentId}
                                                archive={archiveAdmin}
                                                downloadVisualsArchive={downloadVisualsArchiveAdmin}
                                                downloadArchiveUrl={downloadArchiveAdminUrl}
                                                isAutoDownload={true}
                                              />
                                            }
                                          </Fragment>
                                        }

                                        {originalType === VisualFileType.RAW && (!isVirtualVisit || isVirtualVisitMatterport || (isVirtualVisit && isFloorPlan)) &&
                                          <Fragment>
                                            <hr className="section-divider" />
                                            <div className="part">
                                              <h2>{t('editing_section')}</h2>
                                              {canSelectVisuals &&
                                                <Fragment>
                                                  {fileSelection()}
                                                </Fragment>
                                              }
                                              {editorProducts &&
                                                <select
                                                  name="editorProducts"
                                                  id="editorProducts"
                                                  onChange={e => setEditorProduct(ESProduct[e.target.value as ESProduct] ? e.target.value as ESProduct : NONE_EDITOR_PRODUCT)}
                                                  value={editorProduct}
                                                  disabled={!canEditEditorProduct}
                                                >
                                                  <option value={NONE_EDITOR_PRODUCT} disabled>{NONE_EDITOR_PRODUCT}</option>
                                                  {editorProducts.map(eProduct => (
                                                    <option value={eProduct} key={eProduct}>{eProduct}</option>
                                                  ))}
                                                </select>
                                              }
                                              <br />
                                              <p>
                                                <strong>{t('admin_comments_for_editor')}</strong>
                                                {isBKBNDecide &&
                                                  <Badge color="orange" type="fill" className="title-badge">
                                                    {t('bkbn_decide')}
                                                  </Badge>
                                                }
                                              </p>
                                              {assignmentVisualsAlreadySentToEditor && !canEditEditorComments ?
                                                <BlockInfo className="blue">
                                                  {multilineText(adminCommentsForEditor)}
                                                </BlockInfo>
                                                :
                                                <AutoFireActionInput
                                                  value={adminCommentsForEditor}
                                                  request={adminCommentsForEditorResponse}
                                                  action={() => editAdminCommentsForEditorMutation.mutate({ assignmentId, comments: adminCommentsForEditor })}
                                                  render={(className, state) => (
                                                    <InputWrap label="">
                                                      <textarea
                                                        name="adminCommentsForEditor"
                                                        id="adminCommentsForEditor"
                                                        className={className}
                                                        value={adminCommentsForEditor}
                                                        onChange={e => setAdminCommentsForEditor(e.target.value)}
                                                        disabled={!canEditEditorComments}
                                                      >
                                                      </textarea>
                                                    </InputWrap>
                                                  )}
                                                />
                                              }
                                            </div>
                                            <div className="part">
                                              <div className="flex">
                                                <Button
                                                  type="secondary green"
                                                  disabled={!canSendAssignmentVisualsToEditor}
                                                  onClick={initiateSendAssignmentVisualsToEditor}
                                                  textAndIcon={true}
                                                  className="space-right"
                                                >
                                                  <Icon icon={IconType.CHECK} />
                                                  <span>{t('send_to_editor')}</span>
                                                </Button>

                                                {showSendAnyway &&
                                                  <Button
                                                    type="secondary orange"
                                                    disabled={!canSendAnyway}
                                                    onClick={initiateSendAnyway}
                                                    className="space-right"
                                                  >
                                                    {t('send_anyway')}
                                                  </Button>
                                                }

                                                {showSendAnyway && !canSendAnyway && cannotSendAnywayInfo &&
                                                  <span className="space-right">{cannotSendAnywayInfo}</span>
                                                }

                                                {showNoOpenEsoftOrderNotice &&
                                                  <span className="space-right">{t('no_open_esoft_order')}</span>
                                                }

                                                {showSendAnyway &&
                                                  <span className="space-right">{t('esoft_order_open', { reference: openEsoftOrderReference })}</span>
                                                }

                                                {!canSendAssignmentVisualsToEditor && cannotSendAssignmentVisualsToEditorInfo &&
                                                  <span>{cannotSendAssignmentVisualsToEditorInfo}</span>
                                                }
                                              </div>
                                              {sendAssignmentVisualsToEditor &&
                                                <QueryStatus
                                                  query={sendAssignmentVisualsToEditor}
                                                  successMessage={t('sent_to_editor')}
                                                  spaceTopRem={2}
                                                  onPurge={() => sendAssignmentVisualsToEditor.reset()}
                                                />
                                              }
                                            </div>
                                          </Fragment>
                                        }

                                        {<GalleryAdminCommentSection />}

                                        {originalType !== POST_WATERMARK &&
                                          <hr className="section-divider" />
                                        }
                                      </Fragment>

                                      <Fragment>
                                        <Fragment>
                                          <h2>{t('upload_visuals')}</h2>
                                          {isVirtualVisit ?
                                            <Fragment>
                                              {!virtualVisitURL &&
                                                <p>{t('upload_visuals_description_virtual_visit')}</p>
                                              }
                                            </Fragment>
                                            :
                                            <Fragment>
                                              {originalType === VisualFileType.POST &&
                                                <p>{t('upload_visuals_description_post')}</p>
                                              }
                                            </Fragment>
                                          }
                                          {!isVirtualVisit &&
                                            <Fragment>
                                              {(() => {
                                                const getMinCount = () => {
                                                  return 1
                                                }
                                                const minCount = getMinCount()
                                                const maxCount = originalType !== VisualFileType.RAW ? prepurchasedVisualsTotal : uploadMaxCount
                                                const progressTitle = t('upload_minimum', { count: minCount === PIPEDRIVE_INFINITY ? 1 : minCount })
                                                return (
                                                  <Fragment>
                                                    <ProgressCard
                                                      className="part"
                                                      title={progressTitle}
                                                      max={minCount === PIPEDRIVE_INFINITY ? Number.MAX_SAFE_INTEGER : minCount}
                                                      value={visualsCount}
                                                      highlighted={maxCount === PIPEDRIVE_INFINITY ? false : visualsCount > maxCount}
                                                      leftText={(() => {
                                                        if (minCount === PIPEDRIVE_INFINITY) return t('n_uploaded', { count: visualsCount })
                                                        if (visualsCount < minCount) return t('n_remaining', { count: minCount - visualsCount })
                                                        if (originalType !== VisualFileType.RAW && visualsCount > minCount) return t('n_above', { count: visualsCount - minCount })
                                                        if (visualsCount > maxCount) return t('n_above', { count: visualsCount - maxCount })
                                                        return t('n_uploaded', { count: visualsCount })
                                                      })()}
                                                    />
                                                  </Fragment>
                                                )
                                              })()}
                                            </Fragment>
                                          }
                                        </Fragment>
                                        <div className="part">
                                          {(originalType === VisualFileType.POST || isVirtualVisit) &&
                                            <SendVisualsToClient
                                              canSendAssignmentVisualsToClient={canSendAssignmentVisualsToClient}
                                              assignmentId={assignmentId}
                                              visualsCount={visualsCount}
                                              selectMaxValue={prepurchasedVisualsTotal}
                                            />
                                          }

                                          {canRemoveDeliveredVisuals(assignmentStage, isAssignmentCancelled, originalType, selected.size) &&
                                            <Button
                                              type="secondary red"
                                              className="space-right"
                                              onClick={() => setIsRemoveDeliveredVisualsOpen(true)}
                                            >
                                              {t('remove_delivered_visuals', { count: selected.size })}
                                            </Button>
                                          }

                                          {showMatterportTransferInfo &&
                                            <div className="matterport-transfer-info">
                                              <Icon color={Color.SECONDARY_ORANGE_DARK} icon={IconType.DANGER} />
                                              <strong>{t('virtual_visit_needs_transfer', { email: virtualVisitMatterportEmail })}</strong>
                                            </div>
                                          }

                                          {canSelectVisuals && originalType === VisualFileType.RAW &&
                                            <Fragment>
                                              {fileSelection()}
                                            </Fragment>

                                          }

                                          {canDeleteVisuals &&
                                            <Button
                                              type="secondary red"
                                              disabled={visualsCount === 0}
                                              onClick={deleteAllVisuals}
                                              textAndIcon={true}
                                            >
                                              <Icon icon={IconType.TRASH} />
                                              <span>{t('delete_all')}</span>
                                            </Button>
                                          }

                                          {canSubmitVisuals && originalType === VisualFileType.RAW &&

                                            <Box marginTop={2}>
                                              <Button
                                                type="secondary green"
                                                className="space-bottom space-right"
                                                onClick={() => setIsSubmitAssignmentOpen(true)}
                                                disabled={(selected.size === 0 && !isVirtualVisit) || uploadVisualsCount > 0 || visualsRecentlySubmited}
                                              >
                                                {t('submit_assignment')}
                                              </Button>
                                              <Fragment>
                                                {visualsRecentlySubmited ?
                                                  <span>{t('assignment_submitted')}</span>
                                                  :
                                                  (selected.size === 0 && !isVirtualVisit) &&
                                                  <span>{t('no_visuals_selected')}</span>
                                                }
                                              </Fragment>
                                            </Box>
                                          }
                                        </div>
                                      </Fragment>

                                      {originalType === VisualFileType.RAW && canSelectVisualsForStaging &&
                                        <Fragment key="RAW_VISUALS_SELECTION_FOR_STAGING">
                                          <hr className="section-divider" />
                                          <h2>{t('visuals_ready')}</h2>
                                          {!selectedVisualsForStagingExist ?
                                            <Fragment>
                                              <p>{t('visuals_ready_for_staging_description')}</p>
                                              <ProgressCard
                                                className="part"
                                                title={`${(t('select_visuals'))} - ${(product ? (product.quantity > 1 ? `${product.quantity} x ` : '') + t(`product:p_${product.id}`) : 'N/A')}`}
                                                max={prepurchasedVisualsTotal === PIPEDRIVE_INFINITY ? Number.MAX_SAFE_INTEGER : prepurchasedVisualsTotal}
                                                value={selected.size}
                                                highlighted={selected.size > prepurchasedVisualsTotal}
                                                leftText={
                                                  selected.size === prepurchasedVisualsTotal || prepurchasedVisualsTotal === PIPEDRIVE_INFINITY
                                                    ?
                                                    t('all_set')
                                                    :
                                                    selected.size > prepurchasedVisualsTotal
                                                      ?
                                                      t('n_above', { count: selected.size - prepurchasedVisualsTotal })
                                                      :
                                                      t('n_remaining', { count: prepurchasedVisualsTotal - selected.size })
                                                }
                                              />
                                            </Fragment>
                                            :
                                            <Fragment>
                                              <p>{t('visuals_ready_for_staging_selected_description')}</p>
                                              <ProgressCard
                                                className="part"
                                                title={t('selected_visuals')}
                                                max={selectedVisualsForStagingCount}
                                                value={selectedVisualsForStagingCount}
                                                highlighted={true}
                                                color="green"
                                                leftText={t('n_visuals_selected', { count: selectedVisualsForStagingCount })}
                                              />
                                            </Fragment>
                                          }
                                          {canSelectVisuals &&
                                            <Fragment>
                                              {fileSelection()}
                                            </Fragment>
                                          }
                                          <BlockInfo className="part">
                                            <p className="select-visuals-instruction">
                                              <Trans t={t} i18nKey="select_visuals_for_staging_instruction">
                                                <a href={virtualFurnitureHomeLink} rel="nofollow noopener noreferrer" target="_blank">&nbsp;</a>
                                              </Trans>
                                            </p>
                                          </BlockInfo>
                                          <br />
                                          <Button
                                            type="secondary green"
                                            className="space-bottom space-right"
                                            // onClick={}
                                            disabled={!canSubmitVisualsForStaging}
                                          >
                                            {t('validate')}
                                          </Button>
                                          <Fragment>
                                            <span>{cannotSubmitVisualsForStagingInfo}</span>
                                          </Fragment>

                                          <QueryStatus
                                            query={submitAssignment}
                                            successMessage={t('assignment_submitted')}
                                            spaceBottomRem={2}
                                          />

                                        </Fragment>
                                      }
                                      {originalType !== VisualFileType.RAW &&
                                        <Fragment>
                                          <hr className="section-divider" />

                                          <h2>{t('visuals_ready')}</h2>

                                          {!isVirtualVisit ?
                                            <Fragment>
                                              <p>
                                                {!purchasedVisualsExist ?
                                                  t('visuals_ready_description')
                                                  :
                                                  !isAdditionalVisualPurchaseDisabled ?
                                                    t('visuals_ready_purchased_additional_visuals_description')
                                                    :
                                                    t('visuals_ready_purchased_description')
                                                }
                                              </p>
                                              <ProgressCard
                                                className="part"
                                                title={
                                                  !purchasedVisualsExist
                                                    ?
                                                    `${(t('select_visuals'))} - ${(product ? (product.quantity > 1 ? `${product.quantity} x ` : '') + t(`product:p_${product.id}`) : 'N/A')}`
                                                    :
                                                    t('selected_visuals')
                                                }
                                                underTitleText={t('n_visuals_selected', { count: selected.size })}
                                                max={
                                                  !purchasedVisualsExist
                                                    ?
                                                    prepurchasedVisualsTotal === PIPEDRIVE_INFINITY ? Number.MAX_SAFE_INTEGER : prepurchasedVisualsTotal
                                                    :
                                                    (selectedAdditionalVisualsCount > 0 && !isArchiveDisabled) ? selectedAdditionalVisualsCount : purchasedVisualsCount
                                                }
                                                value={selected.size}
                                                highlighted={!purchasedVisualsExist ? selected.size > prepurchasedVisualsTotal : true}
                                                color={selectedNotPurchasedVisualsCount > 0 ? 'orange' : 'green'}
                                                leftText={
                                                  !purchasedVisualsExist
                                                    ?
                                                    (selected.size === prepurchasedVisualsTotal || prepurchasedVisualsTotal === PIPEDRIVE_INFINITY)
                                                      ?
                                                      t('all_set')
                                                      :
                                                      selected.size > prepurchasedVisualsTotal
                                                        ?
                                                        (selectedAdditionalVisualsCount > 0 && !isArchiveDisabled && pricePerAdditionalVisual && discount.gt(0))
                                                          ?
                                                          t('n_extra', { count: selected.size - prepurchasedVisualsTotal })
                                                          :
                                                          t('n_above', { count: selected.size - prepurchasedVisualsTotal })
                                                        :
                                                        t('n_remaining', { count: prepurchasedVisualsTotal - selected.size })
                                                    :
                                                    (selectedAdditionalVisualsCount > 0 && !isArchiveDisabled)
                                                      ?
                                                      t('n_extra', { count: selectedAdditionalVisualsCount })
                                                      :
                                                      undefined
                                                }
                                                textWithLineThrough={
                                                  (selectedAdditionalVisualsCount > 0 && !isArchiveDisabled && pricePerAdditionalVisual && discount.gt(0))
                                                    ?
                                                    formatPrice(totalForAdditionalVisualsBeforeDiscount, currency)
                                                    :
                                                    undefined
                                                }
                                                rightText={
                                                  (selectedAdditionalVisualsCount > 0 && !isArchiveDisabled && pricePerAdditionalVisual)
                                                    ?
                                                    formatPrice(totalForAdditionalVisualsAfterDiscount, currency)
                                                    :
                                                    undefined
                                                }
                                                leftTextAdditional={purchasedVisualsExist ? t('n_visuals_purchased', { count: purchasedVisualsCount }) : undefined}
                                                data-for={`additional_visual_price_tooltip_${assignmentId}`}
                                                tooltipData={(
                                                  <div className="tooltip-content">
                                                    <h4>{t('gallery:additional_visual_price:total')}</h4>
                                                    {discount.gt(0) &&
                                                      <strong className="total-before-discount">{formatPrice(totalForAdditionalVisualsBeforeDiscount, currency)}</strong>
                                                    }
                                                    <strong className="total">{formatPrice(totalForAdditionalVisualsAfterDiscount, currency)}</strong>
                                                    <div className="vat">
                                                      <span className="nowrap">{t('gallery:additional_visual_price:vat')} ({formatPrice(vat)} %)</span>
                                                      <strong>{formatPrice(totalVatForAdditionalVisuals, currency)}</strong>
                                                      <span className="nowrap">{t('gallery:additional_visual_price:after_vat')}</span>
                                                      <strong>{formatPrice(totalAfterVatForAdditionalVisuals, currency)}</strong>
                                                    </div>
                                                  </div>
                                                )}
                                              />
                                              {canSelectVisuals &&
                                                <Fragment>
                                                  {fileSelection()}
                                                </Fragment>
                                              }
                                              <BlockInfo className="part">
                                                {isFloorPlan ?
                                                  <Trans t={t} i18nKey={'gallery:questions_support'} values={{ supportEmail }}>
                                                    <strong>
                                                      <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                                                    </strong>
                                                  </Trans>
                                                  :
                                                  <Trans t={t} i18nKey={'gallery:editing_advice'} values={{ supportEmail }}>
                                                    <strong>
                                                      <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                                                    </strong>
                                                  </Trans>
                                                }
                                              </BlockInfo>
                                              {fileTypeSelection()}
                                              <div className="part">
                                                {(!(selectedAdditionalVisualsCount > 0) || isArchiveDisabled) ?
                                                  <Button
                                                    type="primary"
                                                    disabled={isArchiveDisabled && !downloadArchiveUrl}
                                                    onClick={() => initiateArchiveDownload()}
                                                    className="space-right"
                                                  >
                                                    {t('download')}
                                                  </Button>
                                                  :
                                                  <Button
                                                    type="primary"
                                                    onClick={() => initiateArchiveDownload()}
                                                    className="space-right"
                                                  >
                                                    {t('download_and_purchase')}
                                                  </Button>
                                                }
                                                {isArchiveDisabled && initiateDownloadVisualsArchive?.state !== APIRequestState.ERROR &&
                                                  <Fragment>
                                                    {downloadArchiveUrl ?
                                                      <span>{t('download_ready')}</span>
                                                      :
                                                      purchasedVisualsExist
                                                        ?
                                                        <span>{t('download_requested')}</span>
                                                        :
                                                        <Fragment>
                                                          {selected.size === 0 &&
                                                            <span>{t('no_visuals_selected')}</span>
                                                          }
                                                          {prepurchasedVisualsTotal !== PIPEDRIVE_INFINITY &&
                                                            <Fragment>
                                                              {selected.size > 0 && selected.size < prepurchasedVisualsTotal &&
                                                                <span>{t('select_n_more', { count: prepurchasedVisualsTotal - selected.size })}</span>
                                                              }
                                                              {selected.size > prepurchasedVisualsTotal && isAdditionalVisualPurchaseDisabled &&
                                                                <span>{t('select_n_less', { count: selected.size - prepurchasedVisualsTotal })}</span>
                                                              }
                                                            </Fragment>
                                                          }
                                                        </Fragment>
                                                    }
                                                  </Fragment>
                                                }
                                              </div>
                                            </Fragment>
                                            :
                                            <Fragment>
                                              <p>{t('visuals_ready_virtual_visit_description')}</p>
                                              <BlockInfo className="part">
                                                <Trans t={t} i18nKey={'gallery:questions_support'} values={{ supportEmail }}>
                                                  <strong>
                                                    <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                                                  </strong>
                                                </Trans>
                                              </BlockInfo>
                                              {!!virtualVisitURL && !virtualVisitMatterportEmail && !isVirtualVisitFloorfy &&
                                                <VirtualVisitHostingInfo
                                                  hostingExpirationDate={virtualVisitExpirationDate}
                                                  isExpired={virtualVisitExpired}
                                                  assignmentId={assignmentId}
                                                  discount={dealData?.financials.discount}
                                                />
                                              }
                                            </Fragment>
                                          }
                                        </Fragment>
                                      }
                                    </div>
                                  </div>
                                </Fragment>
                              }
                            </Fragment>
                            :
                            <div className="details">
                              <div className="error-wrap">
                                <h3 className="error-heading"><ErrorMessage error_type={'error'} /></h3>
                                <ErrorMessage className="error" error_type={APIRequestErrorType.UNKNOWN_ERROR} />
                                <div className="error-contact-wrap">
                                  <BlockInfo className="error-contact">
                                    <Trans t={t} i18nKey={'gallery:error_contact_us'} values={{ supportEmail }}>
                                      <strong>
                                        <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                                      </strong>
                                    </Trans>
                                  </BlockInfo>
                                </div>
                              </div>
                            </div>
                          }
                        </Fragment>
                      </div>

                      <div className="main-content-wrapper">

                        {isDocumentProduct
                          ? (
                            <Fragment>
                              <DocumentGalleryContent documentUploadType={documentTypeSelect} />
                              <AdminGalleryMessageThreads />
                            </Fragment>
                          )
                          : (
                            <Fragment>
                              {assignmentStage ?
                                <div className={`main-content ${isVirtualVisit ? 'virtual-visit' : ''}`.trim()}>
                                  {canUploadVisuals &&
                                    <Fragment>
                                      <div className={`drop-zone ${isDraggingFile ? 'drag-active' : ''} ${isDragActive ? 'drag-over' : ''} ${visualsCount > 0 ? 'margin-bottom' : ''}`} {...getRootProps()}>

                                        <input {...getInputProps()} />

                                        {isDragActive || isDraggingFile ?
                                          <h3>{t('drop_here')}</h3>
                                          :
                                          <Fragment>
                                            <p>{t('drag_n_drop')}</p>
                                            <Button
                                              type="primary"
                                              className="choose-files"
                                              disabled={originalType === VisualFileType.RAW && currentRawTab !== RawTabs.REQUIRED}
                                            >
                                              {t('choose_button')}
                                            </Button>
                                          </Fragment>
                                        }

                                      </div>
                                    </Fragment>
                                  }

                                  {!isVirtualVisit
                                    /* VISUALS LISTING SECTION */
                                    ? (
                                      <>
                                        {originalType === VisualFileType.RAW &&
                                          <Stack mt={4} mb={2}>
                                            <PageTabs>
                                              {Object.values(RawTabs).map((tab) => (
                                                <PageTab
                                                  key={tab}
                                                  tab={tab}
                                                  activeTab={currentRawTab}
                                                  tabText={t(`raw_visuals_tabs.${tab}`)}
                                                  onClick={() => setCurrentRawTab(tab)}
                                                />
                                              ))}
                                            </PageTabs>
                                            <MUIDivider sx={{ margin: '0px !important' }} />
                                          </Stack>
                                        }
                                        <div className="photo-grid" ref={setVisualsGridRef}>
                                          {visualsListing()}
                                        </div>
                                      </>
                                    )
                                    :
                                    /* VIRTUAL VISIT SECTION */
                                    <Fragment>
                                      {showVirtualVisitInstructions &&
                                        <GalleryVirtualVisitInstructions />
                                      }

                                      {!showVirtualVisitInstructions &&
                                        <div className="virtual-visit-input-wrap">
                                          <label htmlFor="virtual-visit-input">{t('upload_visuals_instructions_virtual_visit_input_label')}</label>
                                          <div className="line">
                                            <input
                                              type="text"
                                              name="virtual-visit-input"
                                              id="virtual-visit-input"
                                              className="virtual-visit-input"
                                              placeholder="Virtual Visit URL"
                                              value={virtualVisitInput}
                                              onChange={e => setVirtualVisitInput(e.target.value)}
                                            />
                                            <MUIButton
                                              className="submit"
                                              type="defaultPrimary"
                                              isLoading={updateVirtualVisit.isPending}
                                              disabled={updateVirtualVisit.isPending || !virtualVisitInput}
                                              onClick={async () => {
                                                const matterportUrl = toVirtualVisitUrl(virtualVisitInput, isVirtualVisitFloorfy)
                                                if (!matterportUrl) {
                                                  spawnErrorToast(t('upload_virtual_visit_instructions.input_error_floorfy'))
                                                  return
                                                }
                                                await showConfirm(t('virtual_visit_update_prompt'))
                                                  && updateVirtualVisit.mutate({
                                                    assignmentId,
                                                    matterportUrl,
                                                  },
                                                    {
                                                      onSuccess: () => {
                                                        spawnSuccessToast(t('virtual_visit_update_success'))
                                                        // [TODO-RQ BKBN-1295] invalidate query in query function instead of dispatch after calling listVisuals() via react-query
                                                        invalidateListVisuals(queryClient, assignmentId)
                                                      },
                                                    }
                                                  )
                                              }}
                                            >
                                              {t('upload_visuals_instructions_virtual_visit_input_submit')}
                                            </MUIButton>
                                          </div>
                                        </div>
                                      }
                                      {!!virtualVisitURL && !showVirtualVisitInstructions &&
                                        <div className="virtual-visit-embed">
                                          <iframe
                                            className="matterport-embed"
                                            title="Virtual Visit"
                                            width="100%"
                                            height="100%"
                                            src={virtualVisitURL}
                                            frameBorder="0"
                                            allowFullScreen
                                            allow="xr-spatial-tracking"
                                          >
                                          </iframe>
                                        </div>
                                      }
                                      {!!virtualVisitURL && !showVirtualVisitInstructions &&
                                        <div className="virtual-visit-share">
                                          <h2>{t('virtual_visit_share')}</h2>
                                          {/* The link types are Matterport URL parameters; a Floorfy link is shared as delivered. */}
                                          {!isVirtualVisitFloorfy && <Fragment>
                                          <div className="radios">
                                            {Object.values(AvailableVirtualVisitLinkTypes).map(linkType => (
                                              <Fragment key={linkType}>

                                                <label className="checkbox" htmlFor={`virtual_visit_link_type_${linkType}`}>
                                                  <input
                                                    type="radio"
                                                    name="virtual_visit_link_type"
                                                    id={`virtual_visit_link_type_${linkType}`}
                                                    value={linkType}
                                                    checked={selectedVirtualVisitLinkType === linkType}
                                                    onChange={e => setSelectedVirtualVisitLinkType(e.target.value as TypeOfAvailableVirtualVisitLinkTypes)}
                                                  />
                                                  <span className="checkmark"></span>
                                                  <span className="label-after">{t(`virtual_visit_link_type.${linkType}`)}</span>
                                                  <MUITooltip
                                                    id={`virtual_visit_link_type_tooltip_${linkType}`}
                                                    type="secondary"
                                                    content={
                                                      <div className="tooltip-content">
                                                        <span>{t(`virtual_visit_link_type_tooltip.${linkType}`)}</span>
                                                      </div>
                                                    }
                                                  >
                                                    <Icon
                                                      icon={IconType.QUESTION}
                                                      colorClass={ColorClass.SECONDARY_ORANGE_DARK}
                                                      className="tooltip-icon"
                                                      data-tip
                                                      data-for={`virtual_visit_link_type_tooltip_${linkType}`}
                                                    />
                                                  </MUITooltip>
                                                </label>
                                              </Fragment>
                                            ))}
                                          </div>
                                          <p>{t(`virtual_visit_link_type_description.${selectedVirtualVisitLinkType}`)}</p>
                                          </Fragment>}
                                          <h3>{t('virtual_visit_link')}</h3>
                                          <div className="input-group">
                                            <input type="text" name="virtual-visit-share-link" id="virtual-visit-share-link" ref={virtualVisitShareLinkRef} readOnly={true} value={virtualVisitShareLink} />

                                            <CopyButton
                                              type="secondary"
                                              copyText={virtualVisitShareLink}
                                              textContent={t('virtual_visit_copy')}
                                              disabled={!virtualVisitShareLinkRef.current || !virtualVisitURL}
                                              onCopy={() => {
                                                if (!virtualVisitShareLinkRef.current) return
                                                virtualVisitShareLinkRef.current.focus()
                                                virtualVisitShareLinkRef.current.select()
                                                virtualVisitShareLinkRef.current.setSelectionRange(0, 99999)
                                              }}
                                            />
                                          </div>

                                          <span className="embedded-header">
                                            <h3>
                                              {t('virtual_visit_embed')}
                                            </h3>
                                            <MUITooltip
                                              id={'virtual_visit_embed_tooltip'}
                                              type="secondary"
                                              content={
                                                <div className="tooltip-content">
                                                  <span>{t('virtual_visit_embed_tooltip')}</span>
                                                </div>
                                              }
                                            >
                                              <Icon
                                                icon={IconType.QUESTION}
                                                colorClass={ColorClass.SECONDARY_ORANGE_DARK}
                                                className="tooltip-icon space-larger-left"
                                                data-tip
                                                data-for={'virtual_visit_embed_tooltip'}
                                              />
                                            </MUITooltip>
                                          </span>

                                          <div className="input-group">
                                            <input type="text" name="virtual-visit-embed-code" id="virtual-visit-embed-code" ref={virtualVisitEmbedCodeRef} readOnly={true} value={virtualVisitEmbedCode} />

                                            <CopyButton
                                              type="secondary"
                                              textContent={t('virtual_visit_copy')}
                                              copyText={virtualVisitEmbedCode}
                                              disabled={!virtualVisitEmbedCodeRef.current || !virtualVisitURL}
                                              onCopy={() => {
                                                if (!virtualVisitEmbedCodeRef.current) return
                                                virtualVisitEmbedCodeRef.current.focus()
                                                virtualVisitEmbedCodeRef.current.select()
                                                virtualVisitEmbedCodeRef.current.setSelectionRange(0, 99999)
                                              }}
                                            />
                                          </div>
                                        </div>
                                      }
                                    </Fragment>
                                  }
                                </div>

                                :
                                <div className="error-wrap">
                                  <h3 className="error-heading"><ErrorMessage error_type={'error'} /></h3>
                                  <ErrorMessage className="error" error_type={APIRequestErrorType.UNKNOWN_ERROR} />
                                  <div className="error-contact-wrap">
                                    <BlockInfo className="error-contact">
                                      <Trans t={t} i18nKey={'gallery:error_contact_us'} values={{ supportEmail }}>
                                        <strong>
                                          <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                                        </strong>
                                      </Trans>
                                    </BlockInfo>
                                  </div>
                                </div>
                              }
                            </Fragment>
                          )
                        }


                      </div>
                    </div>
                  </Fragment>
                </DynamicQueryContent>
              </DynamicQueryContent>
            </Fragment>
          }
        </div>
      </div>
      <Modal
        className={`gallery-lightbox-modal ${selected.has(expandedImageName || '') ? 'selected' : ''} ${purchasedVisualsKeys.has(expandedImageName || '') ? 'purchased' : ''}`}
        modalContentClassName="gallery-lightbox-modal-content"
        isOpen={isLightboxModalOpen}
        onClickOutside={closeLightboxModal}
        onKeyDown={lightboxModalKeyDownHandler}
      >
        <div className="inside">
          <div className="info-header">
            <strong>{t('photo_no')}{expandedImage && webType && expandedImageWebType?.file?.name}</strong>
            {expandedImage && webType &&
              <span>{t('reference')}: #{expandedImageWebType?.file?.name.split('.')[0].split('-')[2] || ''}</span>
            }
            <div className={closeButtonStyles.closeWrap}>
              <Button
                type="secondary nobackground noborder"
                onClick={closeLightboxModal}
              >
                <Icon icon={IconType.CROSS} />
              </Button>
            </div>
          </div>
          <div className="image-box" style={{ backgroundImage: `url(${expandedImage && webType && expandedImageWebType?.signedUrl})` }}>
            {webType && !expandedImage?.[webType]?.file?.name &&
              <ErrorMessage className="error" error_type={APIRequestErrorType.NOTFOUND_ERROR} />
            }
            {webType && expandedImageWebType && expandedImageWebType?.request.error_type && expandedImageWebType?.request.error_type !== APIRequestErrorType.NONE &&
              <ErrorMessage className="error" error_type={expandedImageWebType?.request.error_type} />
            }
            {expandedImage && downloadVisualsCount > 1 &&
              <Fragment>
                <Button
                  className="arrow previous"
                  type="secondary nobackground noborder"
                  onClick={(e => { e.preventDefault(); switchExpandedImage('previous') })}
                >
                  <Icon icon={IconType.CARET_LEFT} />
                </Button>
                <Button
                  className="arrow next"
                  type="secondary nobackground noborder"
                  onClick={(e => { e.preventDefault(); switchExpandedImage('next') })}
                >
                  <Icon icon={IconType.CARET_RIGHT} />
                </Button>
              </Fragment>
            }
            {expandedImage && expandedImageName && canSelectVisuals &&
              <span
                className={`actionbutton check circular large select ${selected.has(expandedImageName || '') ? 'checked' : ''}`}
                onClick={e => toggleSelectOnImage(expandedImageName)}
              >
                <Icon icon={IconType.CHECK} /></span>
            }
          </div>
        </div>
      </Modal>

      <DownloadToastHandler
        assignmentId={assignmentId}
        initiateDownloadVisualsArchive={initiateDownloadVisualsArchive}
        downloadVisualsArchive={downloadVisualsArchive}
        isDownloadTriggered={isClientDownloadTriggered}
      />

      <UpsellPayment
        isOpen={isAdditionalVisualsPurchaseModalOpen}
        assignmentId={assignmentId}
        productId={additionalVisualProductId}
        productQuantity={selectedAdditionalVisualsCount}
        total={!!currency ? { value: decimalFromBig(totalForAdditionalVisualsAfterDiscount), currency, discountPercentage: ZERO_DECIMAL } : null}
        beforeConfirmAction={handleConfirmSelection}
        beforeConfirmActionState={selectVisualsRequest?.state}
        beforeConfirmActionMutation={selectVisualsMutation}
        onClose={() => setIsAdditionalVisualsPurchaseModalOpen(false)}
        afterClosed={handleAfterAdditionalPurchaseModalClose}
        successNode={(
          <div className="av-payment-success">
            <Icon className="av-payment-success-icon" color={Color.SECONDARY_GREEN_DARK} icon={IconType.CHECK} />
            <h2 className="av-payment-success-title">{t('additional_visuals_purchase.success_title')}</h2>
            <p className="av-payment-success-text">{t('additional_visuals_purchase.success_text')}</p>
            <Button
              type="primary"
              onClick={() => {
                setIsAdditionalVisualsPurchaseModalOpen(false)
                archiveClientMutation.mutate({ assignmentId, types: transformTypes(), filenames: Array.from(selected), isAdmin: true })
              }}>
              {t('download')}
            </Button>
          </div>
        )}
      >
        {/* Using Trans to render tags like <strong> and <br /> */}
        <Trans t={t} i18nKey="purchase_more_visuals_prompt">
          <span></span>
        </Trans>
      </UpsellPayment>
      <div className="gallery-preloader-cache">
        {webType && downloadVisualsEntries.map(([key, allTypes]) => (
          <img key={key} src={allTypes?.[webType]?.signedUrl} onError={(e) => console.log('webType', webType, e)} alt="hidden" />
        ))}
        {thumbnailType && downloadVisualsEntries.map(([key, allTypes]) => (
          <img key={key} src={allTypes?.[thumbnailType]?.signedUrl} onError={(e) => console.log('thumbnailType', thumbnailType, e)} alt="hidden" />
        ))}
      </div>

      {/* REMOVE DELIVERED VISUALS MODAL */}
      <RemoveDeliveredVisualsModal
        isOpen={isRemoveDeliveredVisualsOpen}
        assignmentId={assignmentId}
        filenames={Array.from(selected)}
        purchasedFilenames={clientPurchasedFilenames}
        onClose={() => setIsRemoveDeliveredVisualsOpen(false)}
        onRemoved={onDeliveredVisualsRemoved}
      />

      {/* SUBMIT ASSIGNMENT MODAL */}
      <SubmitAssignmentModal
        isOpen={isSubmitAssignmentOpen}
        type="visual"
        onSubmit={note => initiateSubmitAssignment(note)}
        onClose={() => setIsSubmitAssignmentOpen(false)}
        mutation={submitAssignment}
        onAfterClosed={() => submitAssignment.reset()}
      />

      <ChangeRemunerationDrawer />

      <GalleryUploadErrorPopup
        isOpen={isUploadErrorPopupOpen}
        onClose={() => {
          visualUploadingTriggered.current = false
          setIsUploadErrorPopupOpen(false)
        }}
      />

      {!!dealData &&
        <AssignmentDetailDrawer
          isOpen={!!selectedAssignment}
          onClose={() => setSelectedAssignmentId(null)}
          assignment={selectedAssignment}
          orderId={dealData.id}
          order={dealData}
          workspaceId={dealData.property?.workspaceId ?? ''}
        />
      }

    </Fragment>
  )
}
